import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Trash2, StickyNote } from 'lucide-react';
import { toast } from 'sonner';
import { getAdminUserNotes, addAdminUserNote, deleteAdminUserNote } from '../services/api';

const when = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('en-IE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/** Internal admin notes against a driver / user. Never shown to the user. */
export const AdminNotesDialog = ({ open, user, onOpenChange, onCountChange }) => {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !user?.id) return;
    setLoading(true);
    getAdminUserNotes(user.id)
      .then(({ data }) => setNotes(data.notes || []))
      .catch(() => toast.error('Could not load notes'))
      .finally(() => setLoading(false));
  }, [open, user?.id]);

  const save = async () => {
    const value = text.trim();
    if (!value || saving) return;
    setSaving(true);
    try {
      const { data } = await addAdminUserNote(user.id, value);
      setNotes((n) => [data, ...n]);
      setText('');
      onCountChange?.(user.id, notes.length + 1);
      toast.success('Note added');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save note');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (noteId) => {
    try {
      await deleteAdminUserNote(user.id, noteId);
      setNotes((n) => {
        const next = n.filter((x) => x.id !== noteId);
        onCountChange?.(user.id, next.length);
        return next;
      });
    } catch {
      toast.error('Could not delete note');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-notes-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StickyNote className="w-4 h-4 text-amber-600" />
            Notes — {user?.name || user?.email}
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-gray-500 -mt-1">
          Internal only. Drivers and customers never see these notes.
        </p>

        <div className="space-y-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add a note about this driver…"
            rows={3}
            maxLength={2000}
            className="text-sm"
            data-testid="admin-note-input"
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={save}
              disabled={!text.trim() || saving}
              className="bg-[#2E7D32] text-white hover:bg-[#1B5E20]"
              data-testid="admin-note-save-btn"
            >
              {saving ? 'Saving…' : 'Add note'}
            </Button>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-3 space-y-2" data-testid="admin-notes-list">
          {loading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : notes.length === 0 ? (
            <p className="text-sm text-gray-400 italic" data-testid="admin-notes-empty">No notes yet.</p>
          ) : (
            notes.map((n) => (
              <div key={n.id} className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2" data-testid={`admin-note-${n.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm text-gray-800 whitespace-pre-wrap flex-1">{n.text}</p>
                  <button
                    onClick={() => remove(n.id)}
                    className="text-gray-400 hover:text-red-600 flex-shrink-0"
                    title="Delete note"
                    data-testid={`admin-note-delete-${n.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="mt-1 text-[11px] text-gray-500">{n.author_name} · {when(n.created_at)}</p>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminNotesDialog;
